import { CheckCircle, Circle, ArrowRight, Clock, CaretDown, CaretUp, Plus, Trash, Star, FileText, X, Check, Tag, ListBullets, PencilSimple, Paperclip, DownloadSimple, Sparkle } from "@phosphor-icons/react";
import { useState } from "react";
import { downloadAuthed } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const stageIcons = {
  completed: <CheckCircle weight="fill" className="w-7 h-7 text-emerald-600" />,
  current: <Circle weight="bold" className="w-7 h-7 text-blue-600 animate-pulse-ring" />,
  pending: <Circle weight="regular" className="w-7 h-7 text-slate-300" />,
};

const ALL_CATEGORIES = [
  { value: "id_verification", label: "ID Verification" },
  { value: "proof_of_funds", label: "Proof of Funds" },
  { value: "contracts", label: "Contracts" },
  { value: "searches", label: "Searches" },
  { value: "mortgage", label: "Mortgage" },
  { value: "correspondence", label: "Correspondence" },
  { value: "other", label: "Other" },
];

function DocList({ docs, testid }) {
  return (
    <div className="ml-6 mt-1 mb-1.5 space-y-1" data-testid={testid}>
      {docs.map((d) => (
        <button key={d.id} type="button"
          onClick={(e) => { e.stopPropagation(); downloadAuthed(`/documents/${d.id}/download`, d.filename).catch(() => toast.error("Could not download this file.")); }}
          data-testid={`doc-download-${d.id}`}
          className="w-full flex items-center gap-2 text-left rounded border border-slate-200 bg-slate-50 px-2 py-1 hover:bg-white transition-colors">
          <FileText weight="bold" className="w-3.5 h-3.5 text-[#100f5d] shrink-0" />
          <span className="text-[11px] text-slate-700 truncate flex-1">{d.filename || "Document"}</span>
          {d.uploaded_at && <span className="text-[10px] text-slate-400 shrink-0">{new Date(d.uploaded_at).toLocaleDateString("en-GB")}</span>}
          <DownloadSimple weight="bold" className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        </button>
      ))}
    </div>
  );
}

export default function ConveyancingTimeline({ stages, onUpdateStage, onAddStage, onRemoveStage, isSolicitor, checklist, onUpdateChecklist, onAddSubcategory, onToggleSubcategory, onDeleteSubcategory, onUpdateSubcategories, onUpdateSubcategory }) {
  const [expandedKey, setExpandedKey] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newStageLabel, setNewStageLabel] = useState("");
  const [afterStageKey, setAfterStageKey] = useState("");
  const [adding, setAdding] = useState(false);
  const [editChecklistKey, setEditChecklistKey] = useState(null);
  const [addCatValue, setAddCatValue] = useState("");
  const [addCustomName, setAddCustomName] = useState("");
  const [addMode, setAddMode] = useState("category");
  const [openDocsKey, setOpenDocsKey] = useState(null);
  const [newSubLabel, setNewSubLabel] = useState("");
  const [editSubsKey, setEditSubsKey] = useState(null); // "category" or "custom"

  const getStatusLabel = (status) => {
    if (status === "completed") return "Completed";
    if (status === "current") return "In Progress";
    return "Pending";
  };

  const handleAddStage = async () => {
    if (!newStageLabel.trim() || !afterStageKey || !onAddStage) return;
    setAdding(true);
    try {
      await onAddStage(newStageLabel.trim(), afterStageKey);
      setAddDialogOpen(false);
      setNewStageLabel("");
      setAfterStageKey("");
    } catch {} finally {
      setAdding(false);
    }
  };

  const getStageChecklist = (stageKey) => {
    return checklist?.find((c) => c.stage_key === stageKey);
  };

  const handleAddChecklistItem = (stageKey, category, customName) => {
    if (!onUpdateChecklist) return;
    const current = getStageChecklist(stageKey);
    const currentItems = current?.required?.map((r) => ({ name: r.name, category: r.category || "" })) || [];
    if (category) {
      // Category-based add
      if (currentItems.some(i => i.category === category)) return;
      const label = ALL_CATEGORIES.find(c => c.value === category)?.label || category;
      onUpdateChecklist(stageKey, [...currentItems, { name: label, category }]);
      setAddCatValue("");
    } else if (customName?.trim()) {
      // Freeform name add
      if (currentItems.some(i => i.name.toLowerCase() === customName.trim().toLowerCase())) return;
      onUpdateChecklist(stageKey, [...currentItems, { name: customName.trim(), category: "" }]);
      setAddCustomName("");
    }
  };

  const handleRemoveChecklistItem = (stageKey, itemIndex) => {
    if (!onUpdateChecklist) return;
    const current = getStageChecklist(stageKey);
    const currentItems = current?.required?.map((r) => ({ name: r.name, category: r.category || "" })) || [];
    onUpdateChecklist(stageKey, currentItems.filter((_, i) => i !== itemIndex));
  };

  const handleRenameChecklistItem = (stageKey, itemIndex, newName) => {
    if (!onUpdateChecklist || !newName.trim()) return;
    const current = getStageChecklist(stageKey);
    const currentItems = current?.required?.map((r) => ({ name: r.name, category: r.category || "" })) || [];
    currentItems[itemIndex] = { ...currentItems[itemIndex], name: newName.trim() };
    onUpdateChecklist(stageKey, currentItems);
  };

  return (
    <div className="relative" data-testid="conveyancing-timeline">
      {stages?.map((stage, idx) => {
        const isLast = idx === stages.length - 1;
        const isExpanded = expandedKey === stage.key;
        const stageCheck = getStageChecklist(stage.key);
        const hasChecklist = stageCheck && stageCheck.total > 0;
        const isEditing = editChecklistKey === stage.key;

        return (
          <div key={stage.key} className={`relative flex gap-4 animate-fade-in-up stagger-${Math.min(idx + 1, 6)}`} data-testid={`timeline-stage-${stage.key}`}>
            <div className="flex flex-col items-center">
              <div className="flex-shrink-0 z-10 bg-white p-0.5">
                {stageIcons[stage.status] || stageIcons.pending}
              </div>
              {!isLast && (
                <div className={`w-0.5 flex-1 min-h-[40px] ${stage.status === "completed" ? "bg-emerald-200" : "bg-slate-200"}`} />
              )}
            </div>

            <div className={`flex-1 pb-8 ${isLast ? "pb-0" : ""}`}>
              <button onClick={() => setExpandedKey(isExpanded ? null : stage.key)} className="w-full text-left group" data-testid={`stage-toggle-${stage.key}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className={`text-sm font-semibold ${stage.status === "completed" ? "text-slate-900" : stage.status === "current" ? "text-blue-700" : "text-slate-400"}`}
                        style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {stage.label}
                      </h4>
                      {stage.custom && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-50 text-violet-600">
                          <Star weight="fill" className="w-2.5 h-2.5" /> Custom
                        </span>
                      )}
                      {hasChecklist && (
                        <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded ${
                          stageCheck.completed === stageCheck.total
                            ? "bg-emerald-50 text-emerald-600"
                            : "bg-amber-50 text-amber-600"
                        }`} data-testid={`checklist-badge-${stage.key}`}>
                          <FileText weight="fill" className="w-2.5 h-2.5" />
                          {stageCheck.completed}/{stageCheck.total}
                        </span>
                      )}
                      {stage.subcategories?.length > 0 && (
                        <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded ${
                          stage.subcategories.every(s => s.completed) ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"
                        }`} data-testid={`subs-badge-${stage.key}`}>
                          <ListBullets weight="fill" className="w-2.5 h-2.5" />
                          {stage.subcategories.filter(s => s.completed).length}/{stage.subcategories.length}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                        stage.status === "completed" ? "bg-emerald-50 text-emerald-700"
                          : stage.status === "current" ? "bg-blue-50 text-blue-700"
                          : "bg-slate-100 text-slate-400"}`}>
                        {stage.status === "current" && <Clock weight="bold" className="w-3 h-3" />}
                        {getStatusLabel(stage.status)}
                      </span>
                      {stage.completed_at && (
                        <span className="text-xs text-slate-400">
                          {new Date(stage.completed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                        </span>
                      )}
                    </div>
                    {/* Inline subcategory preview for clients (non-solicitors). Solicitors get edit UI in expanded view. */}
                    {!isSolicitor && stage.subcategories?.length > 0 && (
                      <ul className="mt-2 space-y-1" data-testid={`stage-subs-inline-${stage.key}`}>
                        {stage.subcategories.map((sub) => (
                          <li key={sub.id} className="flex items-center gap-2 text-[11px]">
                            <div className={`w-3 h-3 rounded-sm flex items-center justify-center shrink-0 border ${
                              sub.completed ? "bg-emerald-500 border-emerald-500" : "bg-white border-slate-300"
                            }`}>
                              {sub.completed && <Check weight="bold" className="w-2 h-2 text-white" />}
                            </div>
                            <span className={sub.completed ? "text-slate-500 line-through" : "text-slate-700"}>
                              {sub.label}
                            </span>
                            {sub.completed_at && (
                              <span className="text-[10px] text-slate-400">
                                {new Date(sub.completed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <CaretDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                </div>
              </button>

              {isExpanded && (
                <div className="mt-3 space-y-3" data-testid={`stage-detail-${stage.key}`}>
                  {/* Notes + actions */}
                  <div className="p-4 bg-white border border-slate-200 rounded-lg shadow-sm">
                    {stage.notes && (
                      <p className="text-sm text-slate-600 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{stage.notes}</p>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      {isSolicitor && stage.status === "current" && onUpdateStage && (
                        <button onClick={() => onUpdateStage(stage.key, "completed")} data-testid={`complete-stage-${stage.key}`}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors">
                          Mark Complete <ArrowRight weight="bold" className="w-4 h-4" />
                        </button>
                      )}
                      {isSolicitor && stage.status === "completed" && onUpdateStage && (
                        <button onClick={() => onUpdateStage(stage.key, "current")} data-testid={`reopen-stage-${stage.key}`}
                          className="inline-flex items-center gap-2 px-3 py-2 border border-amber-200 text-amber-700 hover:bg-amber-50 text-sm font-medium rounded-lg transition-colors"
                          title="Move this milestone back to In Progress">
                          Reopen Stage
                        </button>
                      )}
                      {isSolicitor && stage.custom && stage.status === "pending" && onRemoveStage && (
                        <button onClick={() => onRemoveStage(stage.key)} data-testid={`remove-stage-${stage.key}`}
                          className="inline-flex items-center gap-1.5 px-3 py-2 border border-red-200 text-red-600 hover:bg-red-50 text-sm font-medium rounded-lg transition-colors">
                          <Trash weight="bold" className="w-3.5 h-3.5" /> Remove
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Document Checklist */}
                  {(hasChecklist || isSolicitor) && (
                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg" data-testid={`stage-checklist-${stage.key}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="text-xs font-semibold text-slate-900 uppercase tracking-wide flex items-center gap-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          <FileText weight="bold" className="w-3.5 h-3.5 text-slate-500" />
                          Required Documents
                        </h5>
                        {isSolicitor && onUpdateChecklist && (
                          <button
                            onClick={() => setEditChecklistKey(isEditing ? null : stage.key)}
                            data-testid={`edit-checklist-${stage.key}`}
                            className="text-[10px] font-medium text-blue-600 hover:underline"
                          >
                            {isEditing ? "Done" : "Edit"}
                          </button>
                        )}
                      </div>

                      {stageCheck?.required?.length > 0 ? (
                        <div className="space-y-1.5">
                          {stageCheck.required.map((item, itemIdx) => {
                            const docKey = `req:${stage.key}:${itemIdx}`;
                            return (
                            <div key={`${item.name}-${itemIdx}`} data-testid={`checklist-item-${stage.key}-${itemIdx}`}>
                              <div className="flex items-center justify-between py-1">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${item.uploaded ? "bg-emerald-600" : "bg-slate-200"}`}>
                                  {item.uploaded ? <Check weight="bold" className="w-2.5 h-2.5 text-white" /> : <X weight="bold" className="w-2.5 h-2.5 text-slate-400" />}
                                </div>
                                {isEditing ? (
                                  <input
                                    defaultValue={item.name}
                                    onBlur={(e) => e.target.value !== item.name && handleRenameChecklistItem(stage.key, itemIdx, e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                                    className="text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded px-1.5 py-0.5 w-full outline-none focus:border-blue-400"
                                    data-testid={`rename-checklist-${stage.key}-${itemIdx}`}
                                  />
                                ) : (
                                  <span className={`text-xs font-medium truncate ${item.uploaded ? "text-slate-900" : "text-slate-500"}`}>{item.name}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0 ml-2">
                                {!isEditing && item.documents?.length > 0 && (
                                  <button type="button" onClick={() => setOpenDocsKey(openDocsKey === docKey ? null : docKey)}
                                    data-testid={`req-docs-toggle-${stage.key}-${itemIdx}`}
                                    className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-600 bg-blue-50 rounded px-1.5 py-0.5 hover:bg-blue-100">
                                    <Paperclip weight="bold" className="w-3 h-3" /> {item.documents.length}
                                  </button>
                                )}
                                {!isEditing && (item.uploaded ? (
                                  <span className="text-[10px] text-emerald-600 font-medium">Uploaded</span>
                                ) : (
                                  <span className="text-[10px] text-amber-600 font-medium">Missing</span>
                                ))}
                                {isEditing && (
                                  <button onClick={() => handleRemoveChecklistItem(stage.key, itemIdx)} data-testid={`remove-checklist-${stage.key}-${itemIdx}`}
                                    className="w-4 h-4 flex items-center justify-center rounded hover:bg-red-100 text-red-400 hover:text-red-600">
                                    <X weight="bold" className="w-2.5 h-2.5" />
                                  </button>
                                )}
                              </div>
                              </div>
                              {openDocsKey === docKey && item.documents?.length > 0 && (
                                <DocList docs={item.documents} testid={`req-docs-${stage.key}-${itemIdx}`} />
                              )}
                            </div>
                          );})}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400 py-1">No required documents defined</p>
                      )}

                      {/* Add item to checklist */}
                      {isEditing && (
                        <div className="mt-3 pt-3 border-t border-slate-200 space-y-2" data-testid={`add-checklist-${stage.key}`}>
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => setAddMode("category")} className={`text-[10px] font-medium px-2 py-0.5 rounded-full transition-colors ${addMode === "category" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`} data-testid={`add-mode-category-${stage.key}`}>From Category</button>
                            <button onClick={() => setAddMode("custom")} className={`text-[10px] font-medium px-2 py-0.5 rounded-full transition-colors ${addMode === "custom" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`} data-testid={`add-mode-custom-${stage.key}`}>Custom Name</button>
                          </div>
                          {addMode === "category" ? (
                            <div className="flex items-center gap-2">
                              <Select value={addCatValue} onValueChange={setAddCatValue}>
                                <SelectTrigger className="h-7 text-xs flex-1"><SelectValue placeholder="Select category..." /></SelectTrigger>
                                <SelectContent>
                                  {ALL_CATEGORIES.filter((c) => !stageCheck?.required?.some((r) => r.category === c.value)).map((c) => (
                                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button onClick={() => handleAddChecklistItem(stage.key, addCatValue, null)} disabled={!addCatValue}
                                data-testid={`add-checklist-btn-${stage.key}`} className="h-7 px-2.5 text-xs bg-slate-900 hover:bg-slate-800 text-white">
                                <Plus weight="bold" className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Input value={addCustomName} onChange={(e) => setAddCustomName(e.target.value)}
                                placeholder="e.g. Certified Copy of Passport" className="h-7 text-xs flex-1"
                                onKeyDown={(e) => e.key === "Enter" && addCustomName.trim() && handleAddChecklistItem(stage.key, null, addCustomName)}
                                data-testid={`add-custom-name-input-${stage.key}`} />
                              <Button onClick={() => handleAddChecklistItem(stage.key, null, addCustomName)} disabled={!addCustomName.trim()}
                                data-testid={`add-custom-name-btn-${stage.key}`} className="h-7 px-2.5 text-xs bg-slate-900 hover:bg-slate-800 text-white">
                                <Plus weight="bold" className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Subcategories / Sub-tasks */}
                  <div className="p-4 bg-white border border-slate-200 rounded-lg" data-testid={`stage-subcategories-${stage.key}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="text-xs font-semibold text-slate-900 uppercase tracking-wide flex items-center gap-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        <ListBullets weight="bold" className="w-3.5 h-3.5 text-slate-500" />
                        Sub-tasks
                      </h5>
                      {isSolicitor && (
                        <button
                          onClick={() => setEditSubsKey(editSubsKey === stage.key ? null : stage.key)}
                          data-testid={`edit-subs-${stage.key}`}
                          className="text-[10px] font-medium text-blue-600 hover:underline"
                        >
                          {editSubsKey === stage.key ? "Done" : "Edit"}
                        </button>
                      )}
                    </div>

                    {stage.subcategories?.length > 0 ? (
                      <div className="space-y-1.5">
                        {stage.subcategories.map((sub) => (
                          <div key={sub.id} data-testid={`sub-item-${stage.key}-${sub.id}`}>
                            <div className="flex items-center justify-between py-1">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <button
                                  onClick={() => onToggleSubcategory && onToggleSubcategory(stage.key, sub.id)}
                                  data-testid={`toggle-sub-${stage.key}-${sub.id}`}
                                  className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                                    sub.completed ? "bg-emerald-600 border-emerald-600" : "border-slate-300 hover:border-blue-600"
                                  }`}
                                >
                                  {sub.completed && <Check weight="bold" className="w-2.5 h-2.5 text-white" />}
                                </button>
                                <span className={`text-xs font-medium truncate ${sub.completed ? "text-slate-400 line-through" : "text-slate-700"}`}>
                                  {sub.label}
                                </span>
                                {sub.auto && sub.completed && editSubsKey !== stage.key && (
                                  <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5 shrink-0"
                                    title="Automatically detected from case information" data-testid={`sub-auto-${stage.key}-${sub.id}`}>
                                    <Sparkle weight="fill" className="w-2.5 h-2.5" /> Auto
                                  </span>
                                )}
                                {sub.documents?.length > 0 && editSubsKey !== stage.key && (
                                  <button type="button"
                                    onClick={() => setOpenDocsKey(openDocsKey === `sub:${stage.key}:${sub.id}` ? null : `sub:${stage.key}:${sub.id}`)}
                                    data-testid={`sub-docs-toggle-${stage.key}-${sub.id}`}
                                    className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-600 bg-blue-50 rounded px-1.5 py-0.5 hover:bg-blue-100 shrink-0">
                                    <Paperclip weight="bold" className="w-3 h-3" /> {sub.documents.length}
                                  </button>
                                )}
                                {sub.agent_note_template && editSubsKey !== stage.key && (
                                  <span
                                    className="text-[10px] font-medium text-blue-600 bg-blue-50 rounded px-1.5 py-0.5 shrink-0"
                                    title={`Agent note: ${sub.agent_note_template}`}
                                    data-testid={`sub-has-template-${stage.key}-${sub.id}`}
                                  >
                                    Agent note
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0 ml-2">
                                {sub.completed_at && (
                                  <span className="text-[10px] text-slate-400">
                                    {new Date(sub.completed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                                  </span>
                                )}
                                {editSubsKey === stage.key && isSolicitor && (
                                  <button
                                    onClick={() => onDeleteSubcategory && onDeleteSubcategory(stage.key, sub.id)}
                                    data-testid={`delete-sub-${stage.key}-${sub.id}`}
                                    className="w-4 h-4 flex items-center justify-center rounded hover:bg-red-100 text-red-400 hover:text-red-600"
                                  >
                                    <X weight="bold" className="w-2.5 h-2.5" />
                                  </button>
                                )}
                              </div>
                            </div>
                            {openDocsKey === `sub:${stage.key}:${sub.id}` && sub.documents?.length > 0 && (
                              <DocList docs={sub.documents} testid={`sub-docs-${stage.key}-${sub.id}`} />
                            )}

                            {/* Agent-note template editor — only visible
                                when the solicitor is in Edit mode. Blur
                                saves the template so agents see the
                                custom message next time the milestone
                                ticks. */}
                            {editSubsKey === stage.key && isSolicitor && (
                              <div className="ml-6 mt-1 mb-2" data-testid={`sub-template-edit-${stage.key}-${sub.id}`}>
                                <SubcategoryTemplateInput
                                  stageKey={stage.key}
                                  subId={sub.id}
                                  initialValue={sub.agent_note_template || ""}
                                  onSave={(val) => onUpdateSubcategory && onUpdateSubcategory(stage.key, sub.id, { agent_note_template: val })}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 py-1">No sub-tasks defined</p>
                    )}

                    {/* Add subcategory */}
                    {isSolicitor && (
                      <div className="mt-3 pt-3 border-t border-slate-200" data-testid={`add-sub-form-${stage.key}`}>
                        <div className="flex items-center gap-2">
                          <Input
                            value={expandedKey === stage.key ? newSubLabel : ""}
                            onChange={(e) => setNewSubLabel(e.target.value)}
                            placeholder="Add a sub-task..."
                            className="h-7 text-xs flex-1"
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && newSubLabel.trim()) {
                                onAddSubcategory && onAddSubcategory(stage.key, newSubLabel.trim());
                                setNewSubLabel("");
                              }
                            }}
                            data-testid={`add-sub-input-${stage.key}`}
                          />
                          <Button
                            onClick={() => {
                              if (newSubLabel.trim()) {
                                onAddSubcategory && onAddSubcategory(stage.key, newSubLabel.trim());
                                setNewSubLabel("");
                              }
                            }}
                            disabled={!newSubLabel.trim()}
                            data-testid={`add-sub-btn-${stage.key}`}
                            className="h-7 px-2.5 text-xs bg-slate-900 hover:bg-slate-800 text-white"
                          >
                            <Plus weight="bold" className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Add custom stage button */}
      {isSolicitor && onAddStage && (
        <>
          <button onClick={() => setAddDialogOpen(true)} data-testid="add-custom-stage-btn"
            className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-200 rounded-lg text-sm font-medium text-slate-500 hover:border-blue-300 hover:text-blue-600 transition-colors">
            <Plus weight="bold" className="w-4 h-4" /> Add Custom Stage
          </button>

          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Add Custom Stage</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Stage Name</Label>
                  <Input data-testid="custom-stage-name-input" placeholder="e.g., Property Survey" value={newStageLabel}
                    onChange={(e) => setNewStageLabel(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Insert After</Label>
                  <Select value={afterStageKey} onValueChange={setAfterStageKey}>
                    <SelectTrigger data-testid="after-stage-select"><SelectValue placeholder="Select a stage" /></SelectTrigger>
                    <SelectContent>
                      {stages?.map((s) => (
                        <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleAddStage} data-testid="submit-custom-stage-btn" disabled={adding || !newStageLabel.trim() || !afterStageKey}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white">
                  {adding ? "Adding..." : "Add Stage"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}

/**
 * Inline editor for a subcategory's `agent_note_template` — the custom
 * message posted to the Agent Link "Case updates" feed when this
 * milestone is ticked. Debounced-on-blur save so solicitors don't spam
 * the API while typing.
 */
function SubcategoryTemplateInput({ stageKey, subId, initialValue, onSave }) {
  const [value, setValue] = useState(initialValue);
  return (
    <Input
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        if ((value || "").trim() !== (initialValue || "").trim()) {
          onSave?.((value || "").trim());
        }
      }}
      placeholder="Agent note (optional) — leave blank for default"
      className="h-7 text-[11px] bg-slate-50 border-slate-200 focus:bg-white"
      data-testid={`sub-template-input-${stageKey}-${subId}`}
    />
  );
}
